class SimpleProductBundle extends HTMLElement {
  connectedCallback() {
    this.form = this.closest("section")?.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    this.form?.addEventListener("change", () => this.updateSelectionSummary());
    this.form?.addEventListener("input", () => this.updateSelectionSummary());
    this.load();
  }

  async load() {
    const apiBase = this.dataset.apiBase;
    const handle = this.dataset.productHandle;
    if (!apiBase || !handle) {
      this.hidden = true;
      return;
    }

    try {
      const response = await fetch(`${apiBase}/simple/product?handle=${encodeURIComponent(handle)}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Unable to load bundle");
      if (!payload.enabled || !payload.groups?.length) {
        this.hidden = true;
        return;
      }
      this.bundle = payload;
      this.render(payload);
    } catch (error) {
      this.renderMessage(error.message || "Unable to load compatible products", true);
    }
  }

  render(payload) {
    const discount = Number(payload.discountPct || 0);
    this.innerHTML = `
      <div class="simple-product-bundle__header">
        <p class="simple-product-bundle__title">${this.escape(payload.title || "Add compatible products")}</p>
        ${discount > 0 ? `<span class="simple-product-bundle__discount">Up to ${discount}% on add-ons</span>` : ""}
      </div>
      <div class="simple-product-bundle__groups">
        ${payload.groups.map((group) => this.renderGroup(group)).join("")}
      </div>
      <div class="simple-product-bundle__footer">
        <div class="simple-product-bundle__summary" data-simple-bundle-summary></div>
        <button type="button" class="button button--primary simple-product-bundle__button" data-simple-bundle-submit>
          Add selected bundle to cart
        </button>
        <div class="simple-product-bundle__message" data-simple-bundle-message></div>
      </div>
    `;

    this.querySelector("[data-simple-bundle-submit]")?.addEventListener("click", () => this.addToCart());
    this.querySelectorAll(".simple-product-bundle__group").forEach((group) => {
      group.addEventListener("change", () => this.enforceGroupLimits(group));
      group.addEventListener("input", () => this.enforceGroupLimits(group));
      group.addEventListener("click", (event) => this.handleQuantityClick(event));
      this.enforceGroupLimits(group);
    });
    this.updateSelectionSummary();
  }

  renderGroup(group) {
    return `
      <div class="simple-product-bundle__group" data-group-id="${this.escape(group.id || "")}" data-group-max="${Number(group.max || 2)}">
        <p class="simple-product-bundle__group-title">${this.escape(group.title || "Compatible products")}</p>
        <div class="simple-product-bundle__items">
          ${group.items.map((item) => this.renderItem(item)).join("")}
        </div>
      </div>
    `;
  }

  renderItem(item) {
    const disabled = !item.available;
    const maxQuantity = this.itemMax(item);
    const image = item.imageUrl
      ? `<img src="${this.escape(item.imageUrl)}" alt="${this.escape(item.imageAlt || item.title)}" loading="lazy">`
      : "<span></span>";
    const variantTitle = item.variantTitle && item.variantTitle !== "Default Title" ? ` · ${item.variantTitle}` : "";
    const stock = disabled ? "Out of stock" : (typeof item.inventoryQuantity === "number" ? `${item.inventoryQuantity} available` : "Available");

    return `
      <label class="simple-product-bundle__item${disabled ? " is-disabled" : ""}">
        <input type="checkbox" data-addon-id="${this.escape(item.cartId)}" data-addon-title="${this.escape(item.title)}" data-addon-available="${disabled ? "0" : "1"}" data-addon-max="${maxQuantity}" ${disabled ? "disabled" : ""}>
        ${image}
        <span>
          <span class="simple-product-bundle__item-title">${this.escape(item.title)}</span>
          <span class="simple-product-bundle__item-meta">${this.escape([item.vendor, item.sku].filter(Boolean).join(" · ") + variantTitle)}</span>
          <span class="simple-product-bundle__item-stock">${this.escape(stock)}</span>
        </span>
        <span class="simple-product-bundle__qty-control" data-qty-control>
          <button type="button" class="simple-product-bundle__qty-button" data-qty-step="-1" ${disabled ? "disabled" : ""} aria-label="Decrease quantity">-</button>
          <input class="simple-product-bundle__qty" data-addon-qty type="number" min="1" max="${maxQuantity}" value="${Math.min(maxQuantity, Math.max(1, Number(item.quantity || 1)))}" ${disabled ? "disabled" : ""} aria-label="Magazine quantity">
          <button type="button" class="simple-product-bundle__qty-button" data-qty-step="1" ${disabled ? "disabled" : ""} aria-label="Increase quantity">+</button>
        </span>
      </label>
    `;
  }

  itemMax(item) {
    const inventory = Number(item.inventoryQuantity);
    if (Number.isFinite(inventory) && inventory > 0) {
      return Math.max(1, Math.min(10, Math.floor(inventory)));
    }
    return 10;
  }

  selectedParentVariantId() {
    const formVariant = this.form?.querySelector('[name="id"]')?.value;
    return formVariant || null;
  }

  selectedParentQuantity() {
    const value = Number(this.form?.querySelector('[name="quantity"]')?.value || 1);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  selectedAddons() {
    const addons = [];
    this.querySelectorAll(".simple-product-bundle__group").forEach((group) => {
      const max = this.groupMax(group);
      let used = 0;

      Array.from(group.querySelectorAll('[data-addon-id]:checked')).forEach((input) => {
        const label = input.closest("label");
        const qtyInput = label?.querySelector(".simple-product-bundle__qty");
        let quantity = this.quantityValue(qtyInput);

        if (Number.isFinite(max)) {
          quantity = Math.min(quantity, Math.max(0, max - used));
        }

        if (quantity > 0) {
          addons.push({
            id: input.dataset.addonId,
            quantity,
          });
          used += quantity;
        }
      });
    });
    return addons;
  }

  groupMax(group) {
    const max = Number(group?.dataset.groupMax || 0);
    return Number.isFinite(max) && max > 0 ? max : Infinity;
  }

  quantityValue(qtyInput) {
    const min = Number(qtyInput?.min || 1);
    const max = Number(qtyInput?.max || 10);
    const value = Number(qtyInput?.value || min);
    return Math.max(
      Number.isFinite(min) ? min : 1,
      Math.min(Number.isFinite(max) ? max : 10, Number.isFinite(value) ? value : 1)
    );
  }

  handleQuantityClick(event) {
    const button = event.target?.closest?.("[data-qty-step]");
    if (!button) return;

    const label = button.closest("label");
    const checkbox = label?.querySelector("[data-addon-id]");
    const qtyInput = label?.querySelector(".simple-product-bundle__qty");
    if (!checkbox || !qtyInput || checkbox.dataset.addonAvailable !== "1") return;

    checkbox.checked = true;
    const step = Number(button.dataset.qtyStep || 0);
    qtyInput.value = String(this.quantityValue(qtyInput) + step);
    this.enforceGroupLimits(button.closest(".simple-product-bundle__group"));
  }

  enforceGroupLimits(group) {
    const max = this.groupMax(group);
    let used = 0;
    const checkboxes = Array.from(group.querySelectorAll("[data-addon-id]"));

    checkboxes.forEach((checkbox) => {
      const qtyInput = checkbox.closest("label")?.querySelector(".simple-product-bundle__qty");
      if (!checkbox.checked) return;

      let quantity = this.quantityValue(qtyInput);
      if (Number.isFinite(max)) {
        const remaining = Math.max(0, max - used);
        quantity = Math.min(quantity, remaining);
      }

      if (quantity <= 0) {
        checkbox.checked = false;
        return;
      }

      if (qtyInput) {
        qtyInput.value = String(quantity);
      }
      used += quantity;
    });

    checkboxes.forEach((checkbox) => {
      const qtyInput = checkbox.closest("label")?.querySelector(".simple-product-bundle__qty");
      const qtyControl = checkbox.closest("label")?.querySelector("[data-qty-control]");
      if (checkbox.dataset.addonAvailable !== "1") return;
      const itemMax = Number(checkbox.dataset.addonMax || qtyInput?.getAttribute("max") || 10);
      const spare = Number.isFinite(max) ? Math.max(0, max - used) : itemMax;
      const current = checkbox.checked ? this.quantityValue(qtyInput) : 0;
      const allowed = Math.max(1, Math.min(itemMax, current + spare));
      if (qtyInput) qtyInput.max = String(allowed);
      checkbox.disabled = Number.isFinite(max) && !checkbox.checked && used >= max;
      qtyControl?.classList.toggle("is-limited", Number.isFinite(max) && used >= max && checkbox.checked && spare === 0);
    });

    this.updateSelectionSummary();
  }

  selectedAddonQuantity() {
    return this.selectedAddons().reduce((sum, addon) => sum + addon.quantity, 0);
  }

  discountForAddonQuantity(quantity) {
    const tiers = Array.isArray(this.bundle?.discountTiers) && this.bundle.discountTiers.length
      ? this.bundle.discountTiers
      : [
          { minQty: 1, discountPct: 2 },
          { minQty: 2, discountPct: 5 },
          { minQty: 3, discountPct: 7 },
        ];
    return tiers.reduce((best, tier) => {
      const minQty = Number(tier.minQty || 0);
      const pct = Number(tier.discountPct || 0);
      return quantity >= minQty && pct > best ? Math.min(7, pct) : best;
    }, 0);
  }

  updateSelectionSummary() {
    const target = this.querySelector("[data-simple-bundle-summary]");
    if (!target) return;

    const addonQuantity = this.selectedAddonQuantity();
    if (!addonQuantity) {
      target.textContent = "Select magazines to see the bundle discount.";
      return;
    }

    const totalAddonQuantity = addonQuantity * this.selectedParentQuantity();
    const pct = this.discountForAddonQuantity(totalAddonQuantity);
    const label = totalAddonQuantity === 1 ? "magazine" : "magazines";
    target.textContent = pct > 0
      ? `${totalAddonQuantity} ${label} selected - ${pct}% off add-ons`
      : `${totalAddonQuantity} ${label} selected`;
  }

  async addToCart() {
    const parentId = this.selectedParentVariantId();
    const parentQuantity = this.selectedParentQuantity();
    const addons = this.selectedAddons();

    if (!parentId) {
      this.renderMessage("Select a product variant first.", true);
      return;
    }

    if (!addons.length) {
      this.renderMessage("Select at least one compatible product.", true);
      return;
    }

    const bundleId = `${this.dataset.productHandle || "bundle"}-${Date.now().toString(36)}`;
    const totalAddonQuantity = this.selectedAddonQuantity() * parentQuantity;
    const discountPct = this.discountForAddonQuantity(totalAddonQuantity);
    const items = [
      {
        id: parentId,
        quantity: parentQuantity,
        properties: {
          _simple_bundle_id: bundleId,
          _simple_bundle_role: "parent",
        },
      },
      ...addons.map((addon) => {
        const properties = {
          _simple_bundle_id: bundleId,
          _simple_bundle_role: "addon",
        };

        if (discountPct > 0) {
          properties._simple_bundle_discount_pct = String(discountPct);
          properties._simple_bundle_effective_discount_pct = String(discountPct);
        }

        return {
          id: addon.id,
          quantity: addon.quantity * parentQuantity,
          properties,
        };
      }),
    ];

    const button = this.querySelector("[data-simple-bundle-submit]");
    button?.setAttribute("aria-busy", "true");
    button?.setAttribute("disabled", "");
    this.renderMessage("");

    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:bundled-sections", { bubbles: true, detail: { sections } }));
      const response = await fetch((window.theme?.routes?.cart_add_url || "/cart/add.js"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const parsedState = await response.json();
      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || "Unable to add bundle");
      }

      if (window.theme?.pubsub && window.theme?.routes?.cart_url) {
        const cartResponse = await fetch(window.theme.routes.cart_url, { headers: { Accept: "application/json" } });
        const cartJson = await cartResponse.json();
        cartJson.sections = parsedState.sections;
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: "simple-product-bundle", cart: cartJson });
        document.querySelector("cart-drawer")?.show?.(button);
      } else {
        window.location.href = "/cart";
      }
    } catch (error) {
      this.renderMessage(error.message || "Unable to add bundle", true);
    } finally {
      button?.removeAttribute("aria-busy");
      button?.removeAttribute("disabled");
    }
  }

  renderMessage(message, isError = false) {
    const target = this.querySelector("[data-simple-bundle-message]") || this;
    target.textContent = message || "";
    target.classList.toggle("is-error", !!isError);
  }

  escape(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }
}

if (!customElements.get("simple-product-bundle")) {
  customElements.define("simple-product-bundle", SimpleProductBundle);
}
